import { useEffect, useMemo, useState } from 'react';
import { apiRequest } from '../apiClient';
import { buildUiHelpers } from './sharedUi';
import { NEWSLETTER_CONSENT_TEXT, NEWSLETTER_CONSENT_VERSION } from './newsletterConsent';

export default function NewsletterPreferences({ COLORS, onStatusChange, refreshKey }) {
  const { Btn } = useMemo(() => buildUiHelpers({ COLORS, th: {} }), [COLORS]);
  const [status, setStatus] = useState('loading');
  const [consent, setConsent] = useState({ text: NEWSLETTER_CONSENT_TEXT, version: NEWSLETTER_CONSENT_VERSION });
  const [selected, setSelected] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => {
    let active = true;
    apiRequest('/me/newsletter').then(data => {
      if (!active) return;
      setStatus(data.status);
      setConsent({ text: data.consent_text || NEWSLETTER_CONSENT_TEXT, version: data.consent_version || NEWSLETTER_CONSENT_VERSION });
      setSelected(data.status === 'subscribed' || data.status === 'pending');
      onStatusChange(data.status);
      setError('');
      setMessage('');
    }).catch(err => { if (active) setError(err.message); });
    return () => { active = false; };
  }, [onStatusChange, refreshKey]);

  useEffect(() => {
    if (status !== 'pending' || busy || !selected) return;
    let active = true;
    async function refreshConfirmation() {
      try {
        const data = await apiRequest('/me/newsletter');
        if (!active) return;
        setStatus(data.status);
        onStatusChange(data.status);
        if (data.status !== 'pending') setMessage('');
      } catch {
        // A background refresh must not interrupt editing or replace save errors.
      }
    }
    window.addEventListener('focus', refreshConfirmation);
    return () => {
      active = false;
      window.removeEventListener('focus', refreshConfirmation);
    };
  }, [status, busy, selected, onStatusChange]);

  async function save() {
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const data = await apiRequest('/me/newsletter', { method: 'PUT', body: { subscribed: selected, consent_version: consent.version } });
      setStatus(data.status);
      onStatusChange(data.status);
      setMessage(data.status === 'pending' ? 'Bitte bestätige die Anmeldung über den Link in deiner E-Mail.' : data.status === 'subscribed' ? 'Dein Newsletter ist aktiv.' : 'Du bist vom Newsletter abgemeldet.');
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  return <section aria-label="Newsletter">
    <h3 style={{ margin: '0 0 14px', fontSize: 15 }}>Newsletter</h3>
    {status !== 'unsubscribed' && <p style={{ color: COLORS.muted, fontSize: 13 }}>{
      { subscribed: 'Angemeldet', pending: 'Bestätigung ausstehend', expired: 'Bestätigungslink abgelaufen', loading: 'Wird geladen...' }[status]
    }</p>}
    <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13 }}>
      <input type="checkbox" checked={selected} disabled={busy || status === 'loading'} onChange={e => setSelected(e.target.checked)} />
      <span>{consent.text} <a href="https://www.einfach-trading.de/rechtliches.html" target="_blank" rel="noreferrer" style={{ color: COLORS.cyan }}>Datenschutz</a></span>
    </label>
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 12 }}>
      <Btn type="button" color="green" onClick={save} disabled={busy || status === 'loading'}>{busy ? 'Speichert...' : status === 'pending' && selected ? 'Bestätigung erneut senden' : 'Newsletter-Einstellung speichern'}</Btn>
    </div>
    {error && <p role="alert" style={{ color: COLORS.red }}>{error}</p>}
    {message && <p role="status" style={{ color: COLORS.green }}>{message}</p>}
  </section>;
}
